import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Upload, Download, Trash2, File, FileText, FileImage, 
  FileVideo, FileAudio, Search, MoreVertical, Eye, Loader2,
  CheckSquare, Square, CloudUpload
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { adjuntosAPI } from '../api';
import { toast } from '../hooks/use-toast';

// Tipos de adjuntos
const TIPOS_ADJUNTO = [
  { value: 'documento', label: 'Documento' },
  { value: 'escrito', label: 'Escrito' },
  { value: 'notificacion', label: 'Notificación' },
  { value: 'resolucion', label: 'Resolución' },
  { value: 'contrato', label: 'Contrato' },
  { value: 'factura', label: 'Factura' },
  { value: 'correspondencia', label: 'Correspondencia' },
  { value: 'acta', label: 'Acta' },
  { value: 'certificado', label: 'Certificado' },
  { value: 'informe', label: 'Informe' },
  { value: 'otro', label: 'Otro' },
];

// Obtener icono según tipo de archivo
const getFileIcon = (mimeType) => {
  if (!mimeType) return <File className="w-5 h-5 text-gray-400" />;
  if (mimeType.startsWith('image/')) return <FileImage className="w-5 h-5 text-blue-500" />;
  if (mimeType.startsWith('video/')) return <FileVideo className="w-5 h-5 text-purple-500" />;
  if (mimeType.startsWith('audio/')) return <FileAudio className="w-5 h-5 text-green-500" />;
  if (mimeType.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" />;
  if (mimeType.includes('word') || mimeType.includes('document')) return <FileText className="w-5 h-5 text-blue-600" />;
  if (mimeType.includes('sheet') || mimeType.includes('excel')) return <FileText className="w-5 h-5 text-green-600" />;
  return <File className="w-5 h-5 text-gray-400" />;
};

// Formatear tamaño de archivo
const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const AdjuntosSection = ({ 
  entityType = 'expediente', // 'expediente' o 'contacto'
  entityId,
  title = 'Adjuntos',
  showHeader = true,
  compact = false 
}) => {
  const [adjuntos, setAdjuntos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [tipoAdjunto, setTipoAdjunto] = useState('documento');
  const [selectedIds, setSelectedIds] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Cargar adjuntos
  const loadAdjuntos = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const response = entityType === 'expediente' 
        ? await adjuntosAPI.getByExpediente(entityId)
        : await adjuntosAPI.getByContacto(entityId);
      setAdjuntos(response.data);
    } catch (error) {
      console.error('Error cargando adjuntos:', error);
    } finally {
      setLoading(false);
    }
  }, [entityId, entityType]);

  useEffect(() => {
    loadAdjuntos();
  }, [loadAdjuntos]);

  // Subir archivos
  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    let successCount = 0;
    
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tipo_adjunto', tipoAdjunto);
      formData.append('descripcion', '');
      
      try {
        if (entityType === 'expediente') {
          await adjuntosAPI.uploadExpediente(entityId, formData);
        } else {
          await adjuntosAPI.uploadContacto(entityId, formData);
        }
        successCount++;
      } catch (error) {
        console.error('Error subiendo archivo:', error);
        toast({
          title: "Error",
          description: `No se pudo subir ${file.name}`,
          variant: "destructive"
        });
      }
    }
    
    if (successCount > 0) {
      toast({
        title: "Archivos subidos",
        description: `${successCount} archivo(s) subido(s) correctamente`
      });
      loadAdjuntos();
    }
    setUploading(false);
  };

  // Manejar drag & drop
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    handleUpload(Array.from(files));
  };

  // Descargar archivo
  const handleDownload = async (adjunto) => {
    try {
      const response = await adjuntosAPI.getUrl(adjunto.id);
      const { url, storage, nombre } = response.data || {};
      if (!url) throw new Error('URL no disponible');

      if (storage === 's3') {
        // URL firmada de S3 (self-authenticating). Abrir en tab nueva.
        window.open(url, '_blank');
      } else {
        // Almacenamiento LOCAL: el endpoint requiere Bearer token. Si hacemos
        // window.open() el navegador no envía el Authorization header
        // (está en localStorage, no en cookie) y sale {"detail":"Not authenticated"}.
        // Solución: fetch autenticado → Blob → <a download>.
        const token = localStorage.getItem('lexduo_token');
        const absoluteUrl = url.startsWith('http')
          ? url
          : `${process.env.REACT_APP_BACKEND_URL}${url}`;
        const r = await fetch(absoluteUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!r.ok) {
          const txt = await r.text().catch(() => '');
          throw new Error(`HTTP ${r.status}: ${txt}`);
        }
        const blob = await r.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = nombre || adjunto.nombre || 'archivo';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(blobUrl);
      }
    } catch (error) {
      console.error('Error descargando adjunto:', error);
      toast({
        title: "Error",
        description: "No se pudo descargar el archivo",
        variant: "destructive"
      });
    }

  };
  // Eliminar archivo
  const handleDelete = async (adjuntoId) => {
    if (!window.confirm('¿Está seguro de eliminar este archivo?')) return;
    try {
      await adjuntosAPI.delete(adjuntoId);
      toast({ title: "Archivo eliminado" });
      loadAdjuntos();
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo eliminar el archivo",
        variant: "destructive"
      });
    }
  };

  // Eliminar seleccionados
  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`¿Eliminar ${selectedIds.length} archivo(s)?`)) return;
    
    try {
      await adjuntosAPI.deleteBatch(selectedIds);
      toast({ title: `${selectedIds.length} archivo(s) eliminado(s)` });
      setSelectedIds([]);
      loadAdjuntos();
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudieron eliminar los archivos",
        variant: "destructive"
      });
    }
  };

  // Descargar seleccionados (ZIP si son multiples)
  const handleDownloadSelected = async () => {
    if (selectedIds.length === 0) return;
    if (selectedIds.length === 1) {
      const adjunto = adjuntos.find(a => a.id === selectedIds[0]);
      if (adjunto) await handleDownload(adjunto);
      return;
    }
    try {
      const response = await adjuntosAPI.downloadZip(selectedIds);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'adjuntos.zip');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({ title: "Error", description: "No se pudieron descargar los archivos", variant: "destructive" });
    }
  };

  // Toggle selección
  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredAdjuntos.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredAdjuntos.map(a => a.id));
    }
  };

  // Filtrar adjuntos
  const filteredAdjuntos = adjuntos.filter(a => 
    a.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.descripcion?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Card className="w-full">
      {showHeader && (
        <CardHeader className="bg-blue-800 text-white rounded-t-lg py-3 px-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <File className="w-5 h-5" />
            {title} ({adjuntos.length})
          </CardTitle>
        </CardHeader>
      )}
      
      <CardContent className={`${showHeader ? 'pt-4' : 'p-4'} space-y-4`}>
        {/* Selector de tipo */}
        <div className="space-y-2">
          <Label className="text-sm text-gray-600">Selecciona el tipo antes de subir un adjunto</Label>
          <Select value={tipoAdjunto} onValueChange={setTipoAdjunto}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIPOS_ADJUNTO.map(tipo => (
                <SelectItem key={tipo.value} value={tipo.value}>{tipo.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Zona de arrastrar y soltar */}
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
            ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            multiple
            onChange={(e) => handleUpload(Array.from(e.target.files || []))}
          />
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
              <p className="text-blue-600 font-medium">Subiendo archivos...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <CloudUpload className="w-10 h-10 text-blue-500" />
              <p className="text-blue-600 font-medium">Arrastra y suelta para subir un fichero</p>
              <p className="text-sm text-gray-500">...o haz click y selecciona uno de tu dispositivo</p>
            </div>
          )}
        </div>

        {/* Barra de acciones */}
        <div className="flex flex-wrap items-center gap-2 border-b pb-3">
          <div className="relative flex-1 min-w-[200px]">
            <span className="text-sm text-gray-600 mr-2">Ficheros subidos</span>
            <div className="inline-flex items-center">
              <Search className="w-4 h-4 text-gray-400 mr-1" />
              <Input
                placeholder="Busca en la tabla"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-8 w-40"
              />
            </div>
          </div>
          
          <div className="flex flex-wrap gap-1">
            <Button 
              size="sm" 
              variant="outline"
              className="bg-blue-600 text-white hover:bg-blue-700"
              onClick={handleDownloadSelected}
              disabled={selectedIds.length === 0}
            >
              <Download className="w-4 h-4 mr-1" />
              Descargar
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              className="bg-blue-600 text-white hover:bg-blue-700"
              onClick={handleDeleteSelected}
              disabled={selectedIds.length === 0}
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Borrar selección
            </Button>
          </div>
        </div>

        {/* Tabla de adjuntos */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">
                  <Checkbox 
                    checked={selectedIds.length === filteredAdjuntos.length && filteredAdjuntos.length > 0}
                    onCheckedChange={toggleSelectAll}
                  />
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Título</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Fecha</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Descripción</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Tipo</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Tamaño</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-gray-500">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Cargando...
                  </td>
                </tr>
              ) : filteredAdjuntos.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-gray-500">
                    No hay datos
                  </td>
                </tr>
              ) : (
                filteredAdjuntos.map((adjunto) => (
                  <tr key={adjunto.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <Checkbox 
                        checked={selectedIds.includes(adjunto.id)}
                        onCheckedChange={() => toggleSelect(adjunto.id)}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {getFileIcon(adjunto.tipo_archivo)}
                        <button
                          onClick={() => window.open(`/preview/${adjunto.id}`, '_blank', 'noopener,noreferrer')}
                          className="font-medium text-blue-600 hover:text-blue-800 hover:underline truncate max-w-[200px] text-left cursor-pointer"
                          title={`Clic para previsualizar: ${adjunto.nombre}`}
                        >
                          {adjunto.nombre}
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {adjunto.fecha_creacion 
                        ? new Date(adjunto.fecha_creacion).toLocaleDateString('es-ES', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })
                        : '-'}
                    </td>
                    <td className="px-3 py-2 text-gray-600 truncate max-w-[150px]" title={adjunto.descripcion}>
                      {adjunto.descripcion || '-'}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className="text-xs">
                        {TIPOS_ADJUNTO.find(t => t.value === adjunto.tipo_adjunto)?.label || adjunto.tipo_adjunto}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {formatFileSize(adjunto.tamano)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => window.open(`/preview/${adjunto.id}`, '_blank', 'noopener,noreferrer')}
                          title="Previsualizar"
                        >
                          <Eye className="w-4 h-4 text-green-600" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDownload(adjunto)}
                          title="Descargar"
                        >
                          <Download className="w-4 h-4 text-blue-600" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDelete(adjunto.id)}
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          
          {/* Paginación info */}
          <div className="flex items-center justify-end gap-2 mt-3 text-sm text-gray-500">
            <span>Items por página:</span>
            <Select defaultValue="10">
              <SelectTrigger className="w-16 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
            <span>-</span>
            <span>({filteredAdjuntos.length})</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdjuntosSection;
